'use client'

import {
  AppInsightsContext,
  ReactPlugin,
} from '@microsoft/applicationinsights-react-js'
import {
  ApplicationInsights,
  ITelemetryPlugin,
  SeverityLevel,
} from '@microsoft/applicationinsights-web'
import { PropsWithChildren, useEffect } from 'react'

export let appInsights: ApplicationInsights | null = null

export const reactPlugin = new ReactPlugin()

// we dont use the official app insights error boundary because we're using next error boundaries
// we just call this in the error boundary instead
export function recordErrorInAppInsights(
  error: Error & { digest?: string },
  errorInfo?: React.ErrorInfo,
  properties?: Record<string, unknown>,
) {
  const appliedProperties = {
    digest: error.digest,
    errorInfo,
    ...properties,
  }

  reactPlugin.trackException(
    {
      exception: error,
      severityLevel: SeverityLevel.Error,
      properties: appliedProperties,
    },
    appliedProperties,
  )
}

export function initAppInsights({
  appId,
  config: { connectionString },
}: {
  appId: string
  config: { connectionString: string; extensions: ITelemetryPlugin[] }
}) {
  const appInsights = new ApplicationInsights({
    config: {
      connectionString: connectionString,
      extensions: [reactPlugin],
      disablePageUnloadEvents: ['unload'], // skip only the unload listener
      enableAutoRouteTracking: true,
      disableAjaxTracking: true,
      autoTrackPageVisitTime: true,
    },
  })

  appInsights.loadAppInsights()

  appInsights.addTelemetryInitializer(item => {
    item.tags ??= []
    item.tags['ai.cloud.role'] = appId
  })

  return appInsights
}

export function AppInsightsProvider({
  appId,
  connectionString,
  children,
}: PropsWithChildren<{ appId: string; connectionString?: string }>) {
  useEffect(() => {
    if (!connectionString) return undefined

    appInsights = initAppInsights({
      appId,
      config: { connectionString, extensions: [reactPlugin] },
    })

    return () => {
      appInsights?.unload()
    }
  }, [appId, connectionString])

  if (!connectionString) return children

  return (
    <AppInsightsContext.Provider value={reactPlugin}>
      {children}
    </AppInsightsContext.Provider>
  )
}
